/* ─────────────────────────────────────────────────────────────────────────
   The 6-axis arm (public/models/zx-arm.glb).

   The model ships with its joints as plain groups at the origin and its pose
   baked into the vertices, so it is rigged here: each joint group is hung
   from a pivot placed on its hub's measured centre, turning about the hub's
   measured axis (principal axis of the hub ring geometry, arm-model metres).

   It is driven by a damped-least-squares IK on the full tool pose, so every
   move in the choreography is just "put the gripper here, facing down".
   ───────────────────────────────────────────────────────────────────────── */
import * as THREE from 'three';
import { PALETTE } from './enclosure';

export const ARM_SCALE = 2.1;

const JOINTS = [
  { node: 'j1_yaw',       p: [0, 0.19, 0],                 a: [0, 1, 0],                lim: [-3.1, 3.1] },
  { node: 'j2_shoulder',  p: [-0.0005, 0.4216, 0.0007],    a: [0.814, 0, 0.581],        lim: [-1.9, 1.9] },
  { node: 'j3_elbow',     p: [0.1414, 0.7624, -0.198],     a: [0.814, 0, 0.581],        lim: [-2.6, 2.6] },
  { node: 'j4_roll',      p: [0.0406, 1.0436, -0.0554],    a: [-0.309, 0.848, 0.432],   lim: [-3.1, 3.1] },
  { node: 'j5_wrist',     p: [0.009, 1.1273, -0.0121],     a: [0.95, 0.3, 0.09],        lim: [-2.9, 2.9] },
  { node: 'j6_tool_roll', p: [-0.0213, 1.2329, -0.0425],   a: [-0.267, 0.926, -0.267],  lim: [-3.1, 3.1] },
];
const FLANGE = new THREE.Vector3(-0.0213, 1.2329, -0.0425);
const TOOL_DIR = new THREE.Vector3(-0.267, 0.926, -0.267).normalize();
const TOOL_LEN = 0.17;   // flange face → centre of the finger pads, arm-model metres
/* Parallel-jaw gripper. The jaws travel along tool-local Z, which is world
   ∓Z while the tool points down — i.e. across the board's short (53 mm) side. */
export const JAW_OPEN = 0.16;    // pad-face distance from centre when open (model m)
export const JAW_PAD = 0.006;    // half-thickness of a pad

/* 6×6 linear solve (Gaussian elimination, partial pivot) */
function solve6(A, b) {
  const n = 6, M = A.map((r, i) => [...r, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c; for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    [M[c], M[p]] = [M[p], M[c]];
    const d = M[c][c] || 1e-9;
    for (let r = c + 1; r < n; r++) { const f = M[r][c] / d; for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k]; }
  }
  const x = new Array(n).fill(0);
  for (let r = n - 1; r >= 0; r--) { let s = M[r][n]; for (let k = r + 1; k < n; k++) s -= M[r][k] * x[k]; x[r] = s / (M[r][r] || 1e-9); }
  return x;
}

export class Arm {
  constructor(gltf) {
    this.root = new THREE.Group(); this.root.name = 'arm';
    const model = gltf.scene;
    this.root.add(model);
    this.mats = [];
    model.traverse(o => {
      if (!o.isMesh) return;
      o.castShadow = true; o.receiveShadow = true;
      o.material = o.material.clone();
      o.material.envMapIntensity = 1;
      this.mats.push(o.material);
    });

    /* Rig at rest, unscaled, so "model space" is world space. The file's
       nodes carry their own matrices; `attach` keeps every part where it is. */
    model.updateMatrixWorld(true);
    const q = new THREE.Quaternion();
    this.joints = JOINTS.map(j => {
      const g = model.getObjectByName(j.node);
      const parent = g.parent;
      parent.updateMatrixWorld(true);
      const piv = new THREE.Group();
      piv.position.copy(parent.worldToLocal(new THREE.Vector3(...j.p)));
      parent.add(piv);
      piv.updateMatrixWorld(true);
      piv.attach(g);
      const axis = new THREE.Vector3(...j.a).normalize().applyQuaternion(parent.getWorldQuaternion(q).invert());
      return { piv, axis, q: 0, lim: j.lim };
    });

    /* parallel-jaw gripper on the flange */
    const g6 = model.getObjectByName('j6_tool_roll');
    this.tool = new THREE.Group();
    this.tool.position.copy(FLANGE).addScaledVector(TOOL_DIR, 0.012);
    this.tool.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), TOOL_DIR);
    model.add(this.tool);
    model.updateMatrixWorld(true);
    g6.attach(this.tool);
    model.scale.setScalar(ARM_SCALE);
    const steel = PALETTE.steel(), graphite = PALETTE.graphite(), red = PALETTE.red(), white = PALETTE.shell();
    this.padMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.8, emissive: 0x000000 });
    this.ledMat = new THREE.MeshBasicMaterial({ color: 0x331010, toneMapped: false });
    [steel, graphite, red, white, this.padMat].forEach(m => this.mats.push(m));
    const add = (geo, mat, x, y, z, parent = this.tool) => { const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.castShadow = true; parent.add(m); return m; };
    add(new THREE.CylinderGeometry(0.036, 0.04, 0.026, 28), steel, 0, 0.013, 0);            // flange adapter
    add(new THREE.BoxGeometry(0.08, 0.05, 0.1), white, 0, 0.052, 0);                         // gripper body
    add(new THREE.BoxGeometry(0.082, 0.01, 0.102), red, 0, 0.033, 0);                        // red band
    add(new THREE.BoxGeometry(0.022, 0.022, 0.36), graphite, 0, 0.09, 0);                    // jaw rail
    const led = add(new THREE.BoxGeometry(0.01, 0.006, 0.018), this.ledMat, 0.041, 0.056, 0.028); led.castShadow = false;
    this.jaws = [-1, 1].map(side => {
      const jaw = new THREE.Group(); this.tool.add(jaw);
      add(new THREE.BoxGeometry(0.045, 0.026, 0.018), white, 0, 0.09, side * 0.009, jaw);        // carriage
      add(new THREE.BoxGeometry(0.036, 0.085, 0.01), graphite, 0, 0.142, side * 0.011, jaw);      // finger
      add(new THREE.BoxGeometry(0.034, 0.026, JAW_PAD * 2), this.padMat, 0, TOOL_LEN, 0, jaw);  // pad (inner face at jaw.z)
      return { jaw, side };
    });
    this.grip = 0; this.jawClosed = 0.13;
    this.setGrip(0);
    this.tcp = new THREE.Object3D();
    this.tcp.position.set(0, TOOL_LEN, 0);
    this.tool.add(this.tcp);

    this._p = new THREE.Vector3(); this._q = new THREE.Quaternion();
    this.opacity = 1;
  }

  getQ() { return this.joints.map(j => j.q); }
  setQ(q) { this.joints.forEach((j, k) => { j.q = q[k]; }); this.apply(); }

  /* Solve a pose once, robustly: try the seed first, then seeded-random restarts,
     and keep the reachable solution closest (in joint space) to the seed, so a
     chain of waypoints stays in one consistent arm posture. */
  solveWaypoint(target, targetQ, seed, rand) {
    const cands = [];
    const run = q0 => {
      this.setQ(q0);
      this.solve(target, targetQ, 220);
      const p = new THREE.Vector3(); this.tcp.getWorldPosition(p);
      const q = this.getQ();
      const d = seed ? q.reduce((acc, x, k) => acc + Math.abs(x - seed[k]), 0) : q.reduce((acc, x) => acc + Math.abs(x), 0);
      cands.push({ e: p.distanceTo(target), q, d });
    };
    if (seed) run(seed);
    for (let r = 0; r < 24; r++) run(this.joints.map(j => (rand() * 2 - 1) * Math.min(2.6, j.lim[1])));
    const ok = cands.filter(c => c.e < 0.003);
    const best = (ok.length ? ok : cands).sort((a, b) => (ok.length ? a.d - b.d : a.e - b.e))[0];
    this.setQ(best.q);
    return best.q;
  }

  apply() {
    this.joints.forEach(j => j.piv.quaternion.setFromAxisAngle(j.axis, j.q));
    this.root.updateMatrixWorld(true);
  }

  tcpPose(pos = new THREE.Vector3(), quat = new THREE.Quaternion()) {
    this.tcp.getWorldPosition(pos); this.tcp.getWorldQuaternion(quat);
    return [pos, quat];
  }

  /* Damped least squares on position + orientation. Warm-starts from the
     current angles, so a continuous target gives a continuous motion. */
  solve(target, targetQ, iters = 14) {
    const e = new Array(6), o = new THREE.Vector3(), a = new THREE.Vector3(), r = new THREE.Vector3();
    const qe = new THREE.Quaternion(), wq = new THREE.Quaternion();
    const W_ORI = 0.35, LAMBDA2 = 0.02;
    for (let it = 0; it < iters; it++) {
      this.apply();
      const [p, q] = this.tcpPose(this._p, this._q);
      e[0] = target.x - p.x; e[1] = target.y - p.y; e[2] = target.z - p.z;
      qe.copy(targetQ).multiply(wq.copy(q).invert());
      if (qe.w < 0) { qe.x *= -1; qe.y *= -1; qe.z *= -1; qe.w *= -1; }
      const s = Math.sqrt(1 - Math.min(1, qe.w * qe.w)), ang = 2 * Math.acos(Math.min(1, qe.w));
      const k = s < 1e-6 ? 0 : ang / s;
      e[3] = qe.x * k * W_ORI; e[4] = qe.y * k * W_ORI; e[5] = qe.z * k * W_ORI;
      if (Math.hypot(e[0], e[1], e[2]) < 1e-4 && Math.hypot(e[3], e[4], e[5]) < 1e-4) break;

      const J = this.joints.map(j => {
        j.piv.getWorldPosition(o);
        a.copy(j.axis).applyQuaternion(j.piv.parent.getWorldQuaternion(wq)).normalize();
        r.subVectors(p, o).cross(a).negate(); // a × (p - o)
        return [r.x, r.y, r.z, a.x * W_ORI, a.y * W_ORI, a.z * W_ORI];
      });
      // (J Jᵀ + λ²I) y = e ;  Δq = Jᵀ y
      const JJt = [...Array(6)].map((_, i) => [...Array(6)].map((__, k2) => {
        let v = 0; for (let j = 0; j < 6; j++) v += J[j][i] * J[j][k2]; return v + (i === k2 ? LAMBDA2 : 0);
      }));
      const y = solve6(JJt, e);
      this.joints.forEach((j, idx) => {
        let d = 0; for (let i = 0; i < 6; i++) d += J[idx][i] * y[i];
        d = Math.max(-0.35, Math.min(0.35, d));
        j.q = Math.max(j.lim[0], Math.min(j.lim[1], j.q + d));
      });
    }
    this.apply();
  }

  /* k: 0 open → 1 closed on a part of half-width `closed` (model m) */
  setGrip(k, closed = this.jawClosed) {
    this.grip = k;
    const z = JAW_OPEN + (closed + JAW_PAD - JAW_OPEN) * k;
    this.jaws.forEach(({ jaw, side }) => { jaw.position.z = side * z; });
    this.ledMat.color.setHex(k > 0.95 ? 0x19e36b : k > 0.05 ? 0xffb020 : 0xff2020);
    this.padMat.emissive.setRGB(0.25 * (k > 0.95 ? 1 : 0), 0, 0);
  }

  setOpacity(o) {
    this.root.visible = o > 0.01;
    this.mats.forEach(m => { m.transparent = o < 0.995; m.opacity = o; m.depthWrite = o > 0.4; });
  }
}
